// Documents on the building (item 653).
//
// A building's client documents used to live on its onboarding card, which
// locks when the card closes, so they disappeared from reach. They now live on
// the building in facility_documents. The newest row of each type is the
// current one; older rows are kept as history and nothing is ever deleted.
// Replacing a document is adding a newer one (Ben, Q3).
//
// Signed contracts from the Contracts module are shown in place, not copied,
// so one contract keeps one home.
//
// Every employee may add or replace (TCSD-0170, Q2 = A); the audit trail is
// the control.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, FolderOpen, Plus } from 'lucide-react';
import { supabase } from '../../../shared/supabase';
import { commitWrite, useReader } from '../../../shared/save-path';
import { errorMessage } from '../../../shared/errors';
import { fmtBareDate } from '../../../shared/format';

export const DOCUMENT_TYPES = [
  { key: 'Client_Contract', label: 'Client contract' },
  { key: 'BSA', label: 'Building service agreement' },
  { key: 'Signed_Proposal', label: 'Proposal / scope of services' },
  { key: 'Insurance_COI', label: 'Insurance certificate' },
  { key: 'Amendment', label: 'Amendment' },
  { key: 'Other', label: 'Other' },
] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number]['key'];

const TYPE_LABEL: Record<string, string> = Object.fromEntries(
  DOCUMENT_TYPES.map((t) => [t.key, t.label]),
);

const SOURCE_LABEL: Record<string, string> = {
  SOP: 'from the onboarding card',
  AM_Collect: 'from AM Collect',
  Manual: 'added here',
};

export interface FacilityDocumentRow {
  document_id: string;
  document_type: string;
  drive_link: string;
  effective_date: string | null;
  expiration_date: string | null;
  uploaded_by_eid: string | null;
  source: string;
  created_at: string;
}

interface SignedContract {
  contract_id: string;
  contract_number: string | null;
  contract_type: string | null;
  signed_at: string | null;
  document_url: string | null;
  sealed_pdf_url: string | null;
}

/** Group by type in the fixed order; newest first inside each type. */
export function groupDocuments(rows: FacilityDocumentRow[]) {
  const sorted = [...rows].sort((a, b) =>
    a.created_at === b.created_at
      ? b.document_id.localeCompare(a.document_id)
      : b.created_at.localeCompare(a.created_at),
  );
  return DOCUMENT_TYPES.map((t) => {
    const of = sorted.filter((r) => r.document_type === t.key);
    return { type: t.key, label: t.label, current: of[0] ?? null, history: of.slice(1) };
  }).filter((g) => g.current);
}

/** A link a person can open: http(s) only. */
export function isOpenableLink(v: string): boolean {
  return /^https?:\/\/\S+$/i.test(v.trim());
}

/**
 * File a document on the building: a new row, newest-of-its-kind wins. The
 * Add or replace form and Ask AI's building card both save through this
 * (item 944). Throws when it did not land.
 */
export async function fileFacilityDocument(input: {
  facilityId: string;
  type: string;
  link: string;
  effective: string;
  expires: string;
}): Promise<string | null> {
  const rows = await commitWrite<{ document_id: string }>({
    label: 'file this document on the building',
    write: supabase
      .from('facility_documents')
      .insert({
        document_id: '', // assigned by the database
        facility_id: input.facilityId,
        document_type: input.type,
        drive_link: input.link.trim(),
        effective_date: input.effective || null,
        expiration_date: input.expires || null,
        source: 'Manual',
      })
      .select('document_id'),
    touches: ['facility_documents'],
  });
  return rows[0]?.document_id ?? null;
}

export function FacilityDocumentsCard({
  facilityId,
  accountId,
  canAdd,
}: {
  facilityId: string;
  accountId: string | null;
  canAdd: boolean;
}) {
  const [rows, setRows] = useState<FacilityDocumentRow[]>([]);
  const [contracts, setContracts] = useState<SignedContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<DocumentType | null>(null);
  const [openHistory, setOpenHistory] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setError(null);
    const docs = await supabase
      .from('facility_documents')
      .select('document_id, document_type, drive_link, effective_date, expiration_date, uploaded_by_eid, source, created_at')
      .eq('facility_id', facilityId);
    if (docs.error) {
      setError(docs.error.message);
      setLoading(false);
      return;
    }
    setRows((docs.data ?? []) as FacilityDocumentRow[]);
    let cq = supabase
      .from('contracts')
      .select('contract_id, contract_number, contract_type, signed_at, document_url, sealed_pdf_url')
      .not('signed_at', 'is', null);
    cq = accountId
      ? cq.or(`facility_id.eq.${facilityId},and(facility_id.is.null,account_id.eq.${accountId})`)
      : cq.eq('facility_id', facilityId);
    const c = await cq;
    setContracts(c.error ? [] : ((c.data ?? []) as SignedContract[]));
    setLoading(false);
  }, [facilityId, accountId]);

  useEffect(() => {
    void load();
  }, [load]);
  useReader(['facility_documents', 'contracts', 'new_account_sop', 'facilities'], load);

  const groups = useMemo(() => groupDocuments(rows), [rows]);
  const empty = !loading && groups.length === 0 && contracts.length === 0;

  return (
    <div id="info-card-documents" className="scroll-mt-24 rounded-xl border border-tcs-border bg-tcs-bg p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-tcs-text">
          <FolderOpen className="h-4 w-4 text-tcs-text-secondary" />
          Documents
        </h3>
        {canAdd && !adding && (
          <button
            type="button"
            onClick={() => setAdding('Client_Contract')}
            className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-tcs-border px-3 text-sm font-medium text-tcs-accent-ink hover:bg-tcs-surface"
          >
            <Plus className="h-4 w-4" /> Add or replace
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-tcs-text-muted">
        The newest of each kind is the current one. Older ones stay below it as history.
      </p>

      {error && <p className="mb-3 text-sm text-st-danger-fg">Could not load documents: {error}</p>}
      {loading && <p className="text-sm text-tcs-text-muted">Loading…</p>}
      {empty && (
        <p className="text-sm text-tcs-text-muted">
          No client documents filed for this building yet.
        </p>
      )}

      {adding && (
        <AddDocumentForm
          facilityId={facilityId}
          initialType={adding}
          onDone={() => {
            setAdding(null);
            void load();
          }}
        />
      )}

      <ul className="divide-y divide-tcs-border">
        {groups.map((g) => (
          <li key={g.type} className="py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">{g.label}</div>
                <DocLink row={g.current!} />
              </div>
              {canAdd && (
                <button
                  type="button"
                  onClick={() => setAdding(g.type as DocumentType)}
                  className="min-h-[44px] shrink-0 rounded-lg px-3 text-sm text-tcs-accent-ink hover:bg-tcs-surface"
                >
                  Replace
                </button>
              )}
            </div>
            {g.history.length > 0 && (
              <div className="mt-1">
                <button
                  type="button"
                  onClick={() => setOpenHistory((h) => ({ ...h, [g.type]: !h[g.type] }))}
                  className="min-h-[44px] text-xs text-tcs-text-secondary underline"
                >
                  {openHistory[g.type] ? 'Hide' : 'Show'} {g.history.length} earlier
                </button>
                {openHistory[g.type] && (
                  <ul className="mt-1 space-y-1 border-l-2 border-tcs-border pl-3">
                    {g.history.map((h) => (
                      <li key={h.document_id} className="text-tcs-text-secondary">
                        <DocLink row={h} muted />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </li>
        ))}
        {contracts.map((c) => (
          <li key={c.contract_id} className="py-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
              Signed in Contracts
            </div>
            <div className="flex flex-wrap items-center gap-x-3 text-sm">
              <Link to={`/money/contracts/${c.contract_id}`} className="min-h-[44px] inline-flex items-center font-medium text-tcs-accent-ink underline">
                {c.contract_number ?? c.contract_id}
              </Link>
              {(c.sealed_pdf_url || c.document_url) && (
                <a
                  href={(c.sealed_pdf_url || c.document_url)!}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-[44px] items-center gap-1 text-tcs-accent-ink"
                >
                  Open <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
              {c.signed_at && <span className="text-xs text-tcs-text-muted">signed {fmtBareDate(c.signed_at.slice(0, 10))}</span>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DocLink({ row, muted }: { row: FacilityDocumentRow; muted?: boolean }) {
  const openable = isOpenableLink(row.drive_link);
  const meta = [
    `filed ${fmtBareDate(row.created_at.slice(0, 10))}`,
    SOURCE_LABEL[row.source] ?? null,
    row.effective_date ? `effective ${fmtBareDate(row.effective_date)}` : null,
    row.expiration_date ? `expires ${fmtBareDate(row.expiration_date)}` : null,
  ].filter(Boolean);
  return (
    <div className={muted ? 'text-sm' : 'text-sm'}>
      {openable ? (
        <a
          href={row.drive_link}
          target="_blank"
          rel="noreferrer"
          className={`inline-flex min-h-[44px] items-center gap-1 break-all ${muted ? 'text-tcs-text-secondary' : 'font-medium text-tcs-accent-ink'}`}
        >
          Open document <ExternalLink className="h-3.5 w-3.5 shrink-0" />
        </a>
      ) : (
        <span className="break-all text-tcs-text">{row.drive_link}</span>
      )}
      <div className="text-xs text-tcs-text-muted">{meta.join(' · ')}</div>
    </div>
  );
}

function AddDocumentForm({
  facilityId,
  initialType,
  onDone,
}: {
  facilityId: string;
  initialType: DocumentType;
  onDone: () => void;
}) {
  const [type, setType] = useState<DocumentType>(initialType);
  const [link, setLink] = useState('');
  const [effective, setEffective] = useState('');
  const [expires, setExpires] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => setType(initialType), [initialType]);

  const valid = isOpenableLink(link);

  const save = async () => {
    if (!valid) {
      setErr('Paste the document’s link (it starts with https://).');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await fileFacilityDocument({ facilityId, type, link, effective, expires });
      onDone();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-3 space-y-3 rounded-lg border border-tcs-border bg-tcs-surface p-3">
      <label className="block text-sm">
        <span className="mb-1 block text-xs font-medium text-tcs-text-secondary">Kind of document</span>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as DocumentType)}
          className="min-h-[44px] w-full rounded-lg border border-tcs-border bg-tcs-bg px-2 text-sm"
        >
          {DOCUMENT_TYPES.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-sm">
        <span className="mb-1 block text-xs font-medium text-tcs-text-secondary">Link (Google Drive or other)</span>
        <input
          type="url"
          inputMode="url"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="https://drive.google.com/…"
          className="min-h-[44px] w-full rounded-lg border border-tcs-border bg-tcs-bg px-2 text-sm"
        />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-tcs-text-secondary">Effective (optional)</span>
          <input
            type="date"
            value={effective}
            onChange={(e) => setEffective(e.target.value)}
            className="min-h-[44px] w-full rounded-lg border border-tcs-border bg-tcs-bg px-2 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium text-tcs-text-secondary">Expires (optional)</span>
          <input
            type="date"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
            className="min-h-[44px] w-full rounded-lg border border-tcs-border bg-tcs-bg px-2 text-sm"
          />
        </label>
      </div>
      <p className="text-xs text-tcs-text-muted">
        The one you add becomes the current {TYPE_LABEL[type]?.toLowerCase()}. Any earlier one stays as history.
      </p>
      {err && <p className="text-sm text-st-danger-fg">{err}</p>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onDone}
          className="min-h-[44px] rounded-lg px-4 text-sm text-tcs-text-secondary hover:bg-tcs-bg"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="min-h-[44px] rounded-lg bg-tcs-accent-ink px-4 text-sm font-semibold text-tcs-accent-on disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
